'use client';

import { BotIcon, ChevronDownIcon, PanelLeftIcon, XIcon } from 'lucide-react';

import { AssistantModalPrimitive } from '@assistant-ui/react';
import { type ComponentPropsWithoutRef, type FC, type MutableRefObject, type Ref, useEffect, useState } from 'react';

import { Thread } from '~/components/assistant-ui/Thread';
import { ThreadList } from '~/components/assistant-ui/ThreadList';
import { TooltipIconButton } from '~/components/assistant-ui/TooltipIconButton';
import { EnterprisePaywall } from '~/components/enterprise-paywall/EnterprisePaywall';
import { INTERCOM_UPGRADE_MESSAGES } from '~/components/enterprise-paywall/intercom';
import { GlazierChatMock } from '~/components/enterprise-paywall/mocks/GlazierChatMock';
import useDraggableLauncher from '~/hooks/useDraggableLauncher';
import useRouteCheck from '~/hooks/useRouteCheck';
import { useUserContext } from '~/react_context/UserContext';
import { useAssistantEnabled } from './AssistantEnabledContext';

const GLAZIER_CHAT_PAYWALL_BULLETS = [
	'Ask questions about your jobs, customers, and schedule in plain English',
	'Surface insights without digging through tables and reports',
	'Generate follow-ups and summaries on demand',
];

// Matches the rendered button (size-11 = 44px) so clamping keeps the whole button on-screen.
const LAUNCHER_SIZE = 44;
const DEFAULT_HORIZONTAL_PADDING = 80; // was the static `right-20`.
const DEFAULT_VERTICAL_PADDING = 20; // was the static `bottom-5`.
const ELEVATED_VERTICAL_PADDING = 96; // lift above the bottom bar on /schedule and the work orders drawer footer (was `bottom-24`).
const STORAGE_KEY = 'glazier:assistant-launcher-pos';

export const AssistantModal: FC = () => {
	const { enableGlazierChat } = useUserContext();
	const { setEnabled } = useAssistantEnabled();
	const routeCheck = useRouteCheck();
	const isElevatedRoute = routeCheck.isExactFileRoutePath('/schedule') || routeCheck.isExactFileRoutePath('/jobs/[jobId]/work-orders');

	const [showThreadList, setShowThreadList] = useState(false);

	// Until the user drags the button, default to the old static corner (lifted above the bottom bar on /schedule and
	// the work orders drawer footer). The hook's `style` drives the Anchor's position; the popover Content stays
	// anchored to it, so it follows for free.
	const { isLoaded, pointerHandlers, style, wasDragRef } = useDraggableLauncher({
		defaultPosition: {
			horizontalPadding: DEFAULT_HORIZONTAL_PADDING,
			side: 'right',
			verticalPadding: isElevatedRoute ? ELEVATED_VERTICAL_PADDING : DEFAULT_VERTICAL_PADDING,
		},
		launcherSize: LAUNCHER_SIZE,
		storageKey: STORAGE_KEY,
	});

	useEffect(() => {
		setEnabled(enableGlazierChat);
	}, [enableGlazierChat, setEnabled]);

	return (
		<AssistantModalPrimitive.Root>
			<AssistantModalPrimitive.Anchor
				className="aui-root aui-modal-anchor fixed z-[9999] size-11"
				// Hide the anchor until the stored position loads so the button doesn't flash at the default corner first.
				style={{ ...style, visibility: isLoaded ? undefined : 'hidden' }}
			>
				<AssistantModalPrimitive.Trigger asChild>
					<AssistantModalButton {...pointerHandlers} wasDragRef={wasDragRef} />
				</AssistantModalPrimitive.Trigger>
			</AssistantModalPrimitive.Anchor>
			<AssistantModalPrimitive.Content
				sideOffset={16}
				style={{ height: '80vh', width: '35vw', minWidth: '380px' }}
				className="aui-root aui-modal-content flex flex-col data-[state=closed]:fade-out-0 data-[state=closed]:slide-out-to-bottom-1/2 data-[state=closed]:slide-out-to-right-1/2 data-[state=closed]:zoom-out data-[state=open]:fade-in-0 data-[state=open]:slide-in-from-bottom-1/2 data-[state=open]:slide-in-from-right-1/2 data-[state=open]:zoom-in z-[9999] overflow-clip overscroll-contain rounded-xl border bg-popover p-0 text-popover-foreground shadow-md outline-none data-[state=closed]:animate-out data-[state=open]:animate-in [&>.aui-thread-root]:bg-inherit [&>.aui-thread-root_.aui-thread-viewport-footer]:bg-inherit"
			>
				<div className="flex h-12 shrink-0 items-center border-b px-4">
					{enableGlazierChat ? (
						<button
							className="flex size-8 items-center justify-center rounded-md border text-muted-foreground transition-colors hover:bg-muted hover:text-foreground"
							onClick={() => setShowThreadList((v) => !v)}
						>
							<PanelLeftIcon className="size-4" />
							<span className="sr-only">Toggle thread list</span>
						</button>
					) : (
						<div className="size-8" />
					)}
					<h2 className="flex-1 text-center font-semibold text-base text-foreground">Glazier Assistant</h2>
					<AssistantModalPrimitive.Trigger asChild>
						<button className="flex size-8 items-center justify-center rounded-md text-muted-foreground transition-colors hover:bg-muted hover:text-foreground">
							<XIcon className="size-4" />
							<span className="sr-only">Close</span>
						</button>
					</AssistantModalPrimitive.Trigger>
				</div>
				<div className="relative flex min-h-0 flex-1">
					{enableGlazierChat ? (
						<>
							<div
								className={`shrink-0 overflow-y-auto border-r bg-popover transition-all duration-200 ${
									showThreadList ? 'w-56 px-2 py-2' : 'w-0'
								}`}
							>
								{showThreadList && <ThreadList />}
							</div>
							<div className="flex min-h-0 min-w-0 flex-1 flex-col">
								<Thread />
							</div>
						</>
					) : (
						<div className="min-h-0 min-w-0 flex-1">
							<EnterprisePaywall
								bullets={GLAZIER_CHAT_PAYWALL_BULLETS}
								description="Your AI co-pilot for jobs, schedules, and customers."
								featureName="Glazier Assistant"
								intercomMessage={INTERCOM_UPGRADE_MESSAGES.glazierChat}
							>
								<GlazierChatMock />
							</EnterprisePaywall>
						</div>
					)}
				</div>
			</AssistantModalPrimitive.Content>
		</AssistantModalPrimitive.Root>
	);
};

type AssistantModalButtonProps = ComponentPropsWithoutRef<'button'> & {
	'data-state'?: 'open' | 'closed';
	'ref'?: Ref<HTMLButtonElement>;
	'wasDragRef': MutableRefObject<boolean>;
};

const AssistantModalButton = ({ 'data-state': state, ref, wasDragRef, ...rest }: AssistantModalButtonProps) => {
	const tooltip = state === 'open' ? 'Close Assistant' : 'Open Assistant';

	return (
		<TooltipIconButton
			variant="default"
			tooltip={tooltip}
			side="left"
			{...rest}
			// Swallow the synthetic click a drag produces so the modal doesn't toggle when the user is repositioning
			// the button (capture-phase, so it runs before the Radix Trigger's bubble-phase onClick on this element).
			onClickCapture={(e) => {
				if (wasDragRef.current) {
					e.preventDefault();
					e.stopPropagation();
					wasDragRef.current = false;
				}
			}}
			className="aui-modal-button size-full touch-none rounded-full shadow transition-transform hover:scale-110 active:scale-90"
			ref={ref}
		>
			<BotIcon
				data-state={state}
				className="aui-modal-button-closed-icon absolute size-6 transition-all data-[state=closed]:rotate-0 data-[state=open]:rotate-90 data-[state=closed]:scale-100 data-[state=open]:scale-0"
			/>

			<ChevronDownIcon
				data-state={state}
				className="aui-modal-button-open-icon absolute size-6 transition-all data-[state=closed]:-rotate-90 data-[state=open]:rotate-0 data-[state=closed]:scale-0 data-[state=open]:scale-100"
			/>
			<span className="aui-sr-only sr-only">{tooltip}</span>
		</TooltipIconButton>
	);
};
